import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { getToken } from '@/services/api/auth.js';

/**
 * A conexao de tempo real.
 *
 * **O websocket acelera; o polling e quem garante.** Os dois convivem de
 * proposito: quando a conexao esta de pe os lacos desaceleram para 30s, e
 * quando ela cai voltam ao ritmo de antes. Nenhuma tela depende de um evento
 * ter chegado -- se ele se perder, o proximo ciclo pega a mesma novidade pelo
 * delta. Trocar o polling por confianca no socket seria trocar "atrasa 2s" por
 * "some a mensagem", que e muito pior num atendimento.
 *
 * **O evento traz sinal, nao conteudo.** Chega `{tipo, ids}`; quem busca o que
 * mudou e a API REST, que ja confere quem pode ver o que. Ver
 * `app/Events/AlgoMudou.php` na API -- a decisao esta explicada la.
 *
 * Sem `window.REVERB` (ambiente sem websocket configurado), tudo aqui vira
 * no-op e a aplicacao roda como antes.
 */
let echo = null;
let conectado = false;
const ouvintes = new Set();
const assinados = new Map();

/** A tela pergunta isto para escolher o ritmo do polling. */
export const estaConectado = () => conectado;

/** Avisa quem se inscreveu que a conexao subiu ou caiu. */
function anunciar() {
    ouvintes.forEach((ouvinte) => {
        try {
            ouvinte(conectado);
        } catch (erro) {
            console.error('tempoReal: ouvinte falhou', erro);
        }
    });
}

export function aoMudarConexao(ouvinte) {
    ouvintes.add(ouvinte);

    return () => ouvintes.delete(ouvinte);
}

export function ligar() {
    if (echo || !window.REVERB) {
        return echo;
    }

    window.Pusher = Pusher;

    echo = new Echo({
        broadcaster: 'reverb',
        key: window.REVERB.chave,
        wsHost: window.REVERB.host,
        wsPort: window.REVERB.porta,
        wssPort: window.REVERB.porta,
        forceTLS: window.REVERB.esquema === 'https',
        enabledTransports: ['ws', 'wss'],

        // A autorizacao de canal passa pela API desta aplicacao, com o mesmo
        // token da sessao. E la que se decide quem assina o que.
        authorizer: (canal) => ({
            authorize: (socketId, retorno) => {
                window.API_ATENDIMENTO
                    .post(
                        '/api/broadcasting/auth',
                        { socket_id: socketId, channel_name: canal.name },
                        { headers: { Authorization: `Bearer ${getToken()}` } },
                    )
                    .then((resposta) => retorno(null, resposta.data))
                    .catch((erro) => retorno(erro, null));
            },
        }),
    });

    const pusher = echo.connector?.pusher;

    pusher?.connection?.bind('connected', () => {
        conectado = true;
        anunciar();
    });

    ['disconnected', 'unavailable', 'failed'].forEach((evento) => {
        pusher?.connection?.bind(evento, () => {
            conectado = false;
            anunciar();
        });
    });

    return echo;
}

/**
 * Assina um canal privado e chama `aoMudar({tipo, ids})`.
 *
 * Devolve a funcao que cancela. Assinar o mesmo canal duas vezes soma
 * ouvintes em vez de abrir outra assinatura -- a lista e a conversa aberta
 * costumam querer o mesmo canal.
 */
export function assinar(canal, aoMudar) {
    if (!ligar()) {
        return () => {};
    }

    if (!assinados.has(canal)) {
        const inscricao = echo.private(canal);
        const handlers = new Set();

        inscricao.listen('.mudou', (dados) => {
            handlers.forEach((h) => {
                try {
                    h(dados);
                } catch (erro) {
                    console.error('tempoReal: tratamento falhou', erro);
                }
            });
        });

        // Canal negado nao e ruido: ou a tela pediu o que nao devia, ou a
        // sessao perdeu acesso. Fica visivel no console e a tela segue no
        // polling.
        inscricao.error?.((erro) => console.warn(`tempoReal: canal ${canal} negado`, erro));

        assinados.set(canal, { inscricao, handlers });
    }

    const { handlers } = assinados.get(canal);
    handlers.add(aoMudar);

    return () => {
        handlers.delete(aoMudar);

        if (handlers.size === 0) {
            echo.leave(canal);
            assinados.delete(canal);
        }
    };
}

/** Sair da aplicacao fecha a conexao -- e as assinaturas com ela. */
export function desligar() {
    assinados.forEach((_, canal) => echo?.leave(canal));
    assinados.clear();
    echo?.disconnect();
    echo = null;
    conectado = false;
    anunciar();
}
